import React from 'react';
import styles from './PieChart.module.css';

interface PieChartData {
  label: string;
  value: number;
  color: string;
}

interface PieChartProps {
  data: PieChartData[];
  size?: number;
  title?: string;
  showBudgetAnalysis?: boolean;
}

const PieChart: React.FC<PieChartProps> = ({ data, size = 200, title, showBudgetAnalysis = false }) => {
  const center = size / 2;
  const radius = size / 2 - 10;
  
  // Calculate total value
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  if (total === 0) {
    return (
      <div className={styles.container}>
        {title && <h3 className={styles.title}>{title}</h3>}
        <div className={styles.noDataContainer} style={{ width: size, height: size }}>
          <div className={styles.noData}>No data available</div>
        </div>
      </div>
    );
  }
  
  // Calculate angles and create path data
  let cumulativeAngle = 0;
  const paths = data.map((item) => {
    const angle = (item.value / total) * 360;
    const startAngle = cumulativeAngle;
    const endAngle = cumulativeAngle + angle;
    cumulativeAngle += angle;
    
    // Convert to radians
    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;
    
    // Calculate coordinates
    const x1 = center + radius * Math.cos(startAngleRad);
    const y1 = center + radius * Math.sin(startAngleRad);
    const x2 = center + radius * Math.cos(endAngleRad);
    const y2 = center + radius * Math.sin(endAngleRad);
    
    // Large arc flag
    const largeArcFlag = angle > 180 ? 1 : 0;
    
    // Path data
    const pathData = [
      `M ${center} ${center}`,
      `L ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      'Z'
    ].join(' ');
    
    return {
      ...item,
      pathData,
      percentage: ((item.value / total) * 100).toFixed(1)
    };
  });

  // Budget targets for 50/30/20 rule
  const budgetTargets = {
    'Needs': 50,
    'Wants': 30,
    'Savings': 20
  };

  // Calculate budget analysis if enabled
  const getBudgetAnalysis = (label: string, percentage: number) => {
    if (!showBudgetAnalysis || !(label in budgetTargets)) {
      return null;
    }
    
    const target = budgetTargets[label as keyof typeof budgetTargets];
    
    // Calculate variance consistently for all categories:
    // All categories: actual - target (positive when over target, negative when under target)
    const variance = percentage - target;
    
    const varianceDisplay = variance >= 0 ? `+${variance.toFixed(1)}%` : `${variance.toFixed(1)}%`;
    
    // Category-specific variance color logic:
    // Savings: positive variance (over target) = GREEN, negative variance (under target) = RED
    // Wants: positive variance (over target) = RED, negative variance (under target) = GREEN
    // Needs: negative variance (under target) = GREEN, positive variance (over target) = NEUTRAL
    let isGood, isBad;
    if (label === 'Savings') {
      isGood = variance > 0; // Exceeding savings target is good (green)
      isBad = variance < 0;  // Under savings target is bad (red)
    } else if (label === 'Wants') {
      isGood = variance < 0; // Under wants target is good (green) - spending less on wants
      isBad = variance > 0;  // Over wants target is bad (red) - overspending on wants
    } else if (label === 'Needs') {
      isGood = variance < 0; // Under needs target is good (green) - efficient spending
      isBad = false;         // Over needs target is neutral
    } else {
      isGood = variance > 0; // Default behavior for other categories
      isBad = variance < 0;
    }
    
    return {
      variance: varianceDisplay,
      target: `${target}%`,
      isGood,
      isBad
    };
  };
  
  return (
    <div className={styles.container}>
      {title && <h3 className={styles.title}>{title}</h3>}
      <div className={styles.chartContainer}>
        <svg width={size} height={size} className={styles.chart}>
          {paths.map((path, index) => (
            <path
              key={index}
              d={path.pathData}
              fill={path.color}
              stroke="#fff"
              strokeWidth="2"
              className={styles.slice}
            />
          ))}
          {/* Add percentage text for each slice */}
          {paths.map((path, index) => {
            const angle = (path.value / total) * 360;
            const startAngle = paths.slice(0, index).reduce((sum, p) => sum + (p.value / total) * 360, 0);
            const midAngle = startAngle + angle / 2;
            const midAngleRad = (midAngle * Math.PI) / 180;
            
            // Position text at 60% of radius from center
            const textRadius = radius * 0.6;
            const textX = center + textRadius * Math.cos(midAngleRad);
            const textY = center + textRadius * Math.sin(midAngleRad);
            
            return (
              <text
                key={`text-${index}`}
                x={textX}
                y={textY}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="white"
                fontSize="14"
                fontWeight="bold"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}
              >
                {path.percentage}%
              </text>
            );
          })}
        </svg>
        <div className={showBudgetAnalysis ? styles.budgetAnalysisLegend : styles.legend}>
          {showBudgetAnalysis ? (
            // Budget analysis view with three columns
            <div className={styles.budgetTable}>
              <div className={styles.budgetHeader}>
                <div className={styles.categoryColumn}>Category</div>
                <div className={styles.varianceColumn}>Budget Variance</div>
                <div className={styles.targetColumn}>Target</div>
              </div>
              {paths.map((item, index) => {
                const analysis = getBudgetAnalysis(item.label, parseFloat(item.percentage));
                return (
                  <div key={index} className={styles.budgetRow}>
                    <div className={styles.categoryCell}>
                      <div 
                        className={styles.legendColor} 
                        style={{ backgroundColor: item.color }}
                      />
                      <span className={styles.legendText}>
                        {item.label}: ${item.value.toFixed(2)} ({item.percentage}%)
                      </span>
                    </div>
                    <div className={
                      analysis?.isBad ? styles.overBudgetCell : 
                      analysis?.isGood ? styles.varianceCell : 
                      styles.neutralVarianceCell
                    }>
                      {analysis?.variance || '-'}
                    </div>
                    <div className={styles.targetCell}>
                      {analysis?.target || '-'}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Standard legend view
            paths.map((item, index) => (
              <div key={index} className={styles.legendItem}>
                <div 
                  className={styles.legendColor} 
                  style={{ backgroundColor: item.color }}
                />
                <span className={styles.legendText}>
                  {item.label}: ${item.value.toFixed(2)} ({item.percentage}%)
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PieChart;